import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActions,
  Stack,
  Chip,
  Divider
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';
import { notesService, Note } from '../services/notesService';
import { useAuth } from '../contexts/AuthContext';

interface NotesProps {
  claimId: string;
}

export const Notes: React.FC<NotesProps> = ({ claimId }) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [newNote, setNewNote] = useState('');
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [editContent, setEditContent] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    loadNotes();
  }, [claimId]);

  const loadNotes = async () => {
    try {
      const fetchedNotes = await notesService.getNotesByClaimId(claimId);
      console.log('Fetched notes:', fetchedNotes);
      setNotes(fetchedNotes);
    } catch (error) {
      console.error('Error loading notes:', error);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      const addedNote = await notesService.createNote(claimId, newNote);
      setNotes([addedNote, ...notes]);
      setNewNote('');
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const handleUpdateNote = async () => {
    if (!editingNote || !editContent.trim()) return;

    try {
      const updatedNote = await notesService.updateNote(editingNote.id, editContent);
      setNotes(notes.map(note => note.id === updatedNote.id ? updatedNote : note));
      setEditingNote(null);
      setEditContent('');
    } catch (error) {
      console.error('Error updating note:', error);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      await notesService.deleteNote(noteId);
      setNotes(notes.filter(note => note.id !== noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  const startEditing = (note: Note) => {
    setEditingNote(note);
    setEditContent(note.content);
  };

  const cancelEditing = () => {
    setEditingNote(null);
    setEditContent('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Add Note Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Add New Note
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          placeholder="Add a new note..."
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Button 
          variant="contained" 
          onClick={handleAddNote} 
          disabled={!newNote.trim()}
          size="large"
        >
          Add Note
        </Button>
      </Paper>

      {/* Notes List */}
      <Stack spacing={2}>
        {notes.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No notes yet. Add your first note above.
            </Typography>
          </Paper>
        ) : (
          notes.map((note) => (
            <Card key={note.id} variant="outlined" sx={{ width: '100%' }}>
              {/* Note Header */}
              <Box sx={{ p: 2, pb: 1, backgroundColor: 'grey.50' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      label={note.creator ? `${note.creator.firstName} ${note.creator.lastName}` : 'Unknown'}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip 
                      label={note.created_at ? formatDate(note.created_at) : "Date unavailable"}
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                    {note.updated_at && note.updated_at !== note.created_at && (
                      <Chip 
                        label={`Updated: ${formatDate(note.updated_at)}`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  
                  {user?.id === note.created_by && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {editingNote?.id === note.id ? (
                        <>
                          <IconButton 
                            size="small" 
                            onClick={handleUpdateNote} 
                            disabled={!editContent.trim()}
                            color="primary"
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={cancelEditing}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton size="small" onClick={() => startEditing(note)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteNote(note.id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
              
              {/* Note Content */}
              <CardContent sx={{ pt: 2 }}>
                {editingNote?.id === note.id ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    variant="outlined"
                  />
                ) : (
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      whiteSpace: 'pre-wrap', 
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      lineHeight: 1.6,
                      fontSize: '0.95rem'
                    }}
                  >
                    {note.content}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </Stack>
    </Box>
  );
}; 